// Resultado do "Testar envio" — mockups "API Resultado Sucesso" e "API
// Resultado Erro" (480px). Substitui o toast: mostra o corpo real devolvido
// pela API, o status HTTP e a latência medida no cliente.
import React from "react";

import { makeStyles, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import EmojiObjectsOutlinedIcon from "@material-ui/icons/EmojiObjectsOutlined";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";

import AerosDialog from "../../components/AerosDialog";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { i18n } from "../../translate/i18n";
import { MONO } from "./apiPageStyles";
import { formatResponseBody, dicaPorStatus, metaDoResultado } from "./apiResultRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const borderSoft = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  return {
    head: { display: "flex", alignItems: "center", gap: 12 },
    headText: { minWidth: 0, display: "flex", flexDirection: "column" },
    headTitle: {
      fontSize: 15,
      fontWeight: 700,
      lineHeight: 1.3,
      color: theme.palette.text.primary,
    },
    headMeta: {
      fontSize: 12,
      fontWeight: 400,
      color: theme.palette.text.secondary,
      marginTop: 2,
    },
    tile: {
      width: 40,
      height: 40,
      borderRadius: 11,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      "& .MuiSvgIcon-root": { fontSize: 22 },
    },
    tileOk: { backgroundColor: alpha(AEROS.success, 0.14), color: AEROS.success },
    tileFail: { backgroundColor: alpha(AEROS.error, 0.14), color: AEROS.error },
    eyebrow: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
    },
    pre: {
      backgroundColor: isLight ? theme.palette.aeros.surface2 : "#0E1119",
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      borderRadius: 10,
      padding: "12px 14px",
      fontFamily: MONO,
      fontSize: 12,
      lineHeight: 1.7,
      color: theme.palette.text.primary,
      margin: 0,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      maxHeight: 260,
      overflowY: "auto",
      ...theme.scrollbarStylesSoft,
    },
    preFail: { borderColor: alpha(AEROS.error, 0.3) },
    hint: {
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      fontSize: 12.5,
      lineHeight: 1.5,
      color: theme.palette.text.primary,
      backgroundColor: alpha(AEROS.warning, 0.06),
      border: `1px solid ${alpha(AEROS.warning, 0.25)}`,
      borderRadius: 10,
      padding: "10px 12px",
      "& .MuiSvgIcon-root": {
        fontSize: 17,
        marginTop: 1,
        flex: "none",
        color: stateText(theme, AEROS.warning, "warning"),
      },
    },
    footer: {
      padding: "14px 20px",
      borderTop: `1px solid ${borderSoft}`,
      display: "flex",
      gap: 10,
      justifyContent: "flex-end",
    },
    ghostButton: {
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.16)"}`,
      color: theme.palette.text.primary,
      borderRadius: 10,
      padding: "8px 14px",
      fontWeight: 600,
      fontSize: 13,
      textTransform: "none",
      "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 15 },
    },
    primaryButton: {
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
      color: "#07080D",
      borderRadius: 10,
      padding: "8px 16px",
      fontWeight: 700,
      fontSize: 13,
      textTransform: "none",
      boxShadow: "none",
    },
  };
});

const ResultDrawer = ({ result, onClose, onCopy }) => {
  const classes = useStyles();
  if (!result) return null;

  const { ok } = result;
  const corpo = formatResponseBody(result.body);
  const dica = ok ? "" : dicaPorStatus(result.status);

  const titulo = ok
    ? i18n.t("aeros.api.result.successTitle")
    : i18n.t("aeros.api.result.errorTitle");

  const footer = (
    <div className={classes.footer}>
      {corpo ? (
        <Button
          className={classes.ghostButton}
          startIcon={<FileCopyOutlinedIcon />}
          onClick={() => onCopy(corpo)}
        >
          {i18n.t("aeros.api.result.copy")}
        </Button>
      ) : null}
      <Button className={classes.primaryButton} onClick={onClose}>
        {i18n.t("aeros.api.result.close")}
      </Button>
    </div>
  );

  return (
    <AerosDialog
      open
      onClose={onClose}
      width={480}
      // O cabeçalho do mockup é [ícone] (título / meta) — vai inteiro no
      // `title` para o ícone ficar ao lado das DUAS linhas, não só da primeira.
      title={
        <span className={classes.head}>
          <span className={`${classes.tile} ${ok ? classes.tileOk : classes.tileFail}`}>
            {ok ? <CheckCircleIcon /> : <ErrorIcon />}
          </span>
          <span className={classes.headText}>
            <span className={classes.headTitle}>{titulo}</span>
            <span className={classes.headMeta}>{metaDoResultado(result)}</span>
          </span>
        </span>
      }
      footer={footer}
    >
      <div className={classes.eyebrow}>{i18n.t("aeros.api.result.response")}</div>
      {corpo ? (
        <pre className={`${classes.pre} ${ok ? "" : classes.preFail}`}>{corpo}</pre>
      ) : null}
      {dica ? (
        <div className={classes.hint}>
          <EmojiObjectsOutlinedIcon />
          <span>{dica}</span>
        </div>
      ) : null}
    </AerosDialog>
  );
};

export default ResultDrawer;
